import { supabase } from '../lib/supabase';

/**
 * Service for managing user garage (collection) operations
 * Handles adding/removing models from user's personal collection
 * UPDATED: Now supports both products AND components
 */
export const userGarageService = {
  /**
   * Get all items (products AND components) in user's garage/collection
   * @param {string} source - Filter by source: 'wishlist', 'saved_for_later', or 'garage'
   */
  async getUserGarage(source = null) {
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError) throw new Error(`Authentication error: ${sessionError.message}`);
      const user = session?.user;
      if (!user) throw new Error('Not authenticated');

      // CRITICAL FIX: Query BOTH products and components, including parent product for components
      let query = supabase
        ?.from('user_garage')?.select(`
          *,
          product:products (
            id,name,description,price,thumbnail_path,gltf_file_path,category_id,manufacturer_id,component_count
          ),
          component:components (
            id,name,custom_name,description,price,manufacturer_part_number,manufacturer_id,product_id,stock_quantity,
            product:products (id, name, thumbnail_path)
          )
        `)?.eq('user_id', user?.id)?.order('added_at', { ascending: false });

      // Filter by source if provided
      if (source) {
        query = query?.eq('source', source);
      }

      const { data, error } = await query;

      if (error) throw error;

      // CRITICAL FIX: Transform data to handle both products AND components
      return data?.map(item => {
        // Determine if this is a product or component
        const isProduct = item?.item_type === 'product' || item?.product;
        const isComponent = item?.item_type === 'component' || item?.component;

        return {
          id: item?.id,
          userId: item?.user_id,
          productId: item?.product_id || null,
          componentId: item?.component_id || null,
          itemType: item?.item_type || (isProduct ? 'product' : 'component'),
          notes: item?.notes,
          addedAt: item?.added_at,
          source: item?.source,
          // Include product data if available
          product: item?.product ? {
            id: item?.product?.id,
            name: item?.product?.name,
            description: item?.product?.description,
            price: item?.product?.price,
            thumbnailPath: item?.product?.thumbnail_path,
            gltfFilePath: item?.product?.gltf_file_path,
            categoryId: item?.product?.category_id,
            manufacturerId: item?.product?.manufacturer_id,
            componentCount: item?.product?.component_count || 0 // NEW: Map component_count
          } : null,
          // CRITICAL FIX: Include component data if available
          component: item?.component ? {
            id: item?.component?.id,
            name: item?.component?.name,
            customName: item?.component?.custom_name, // Map custom_name
            description: item?.component?.description,
            price: item?.component?.price,
            manufacturerPartNumber: item?.component?.manufacturer_part_number,
            manufacturerId: item?.component?.manufacturer_id,
            productId: item?.component?.product_id, // This is the ID of the parent product
            stockQuantity: item?.component?.stock_quantity,
            // Map nested product info
            product: item?.component?.product ? {
                id: item?.component?.product?.id,
                productId: item?.component?.product?.id, // Duplicate for easier UI access
                name: item?.component?.product?.name,
                thumbnailPath: item?.component?.product?.thumbnail_path
            } : null,
            // Use product thumbnail if component image is missing
            imageUrl: item?.component?.images_url?.[0] || item?.component?.product?.thumbnail_path || `https://ui-avatars.com/api/?name=${encodeURIComponent(item?.component?.name || 'Component')}&background=6366f1&color=fff&size=128`
          } : null
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching user garage:', error);
      throw error;
    }
  },

  /**
   * Add a product OR component to user's garage/collection
   * @param {number} itemId - ID of product or component
   * @param {string} notes - Optional notes
   * @param {string} source - Source category: 'wishlist', 'saved_for_later', or 'garage'
   * @param {string} itemType - Type: 'product' or 'component' (optional, will auto-detect)
   */
  async addToGarage(itemId, notes = '', source = 'wishlist', itemType = null) {
    try {
      console.log('🔐 addToGarage called with:', { itemId, notes, source, itemType });

      // ✅ CRITICAL FIX: Enhanced authentication check with detailed logging
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      console.log('🔐 Session check result:', {
        hasSession: !!session,
        hasUser: !!session?.user,
        userId: session?.user?.id,
        sessionError: sessionError?.message
      });
      
      if (sessionError) {
        console.error('❌ Session error in addToGarage:', sessionError);
        throw new Error(`Authentication error: ${sessionError?.message}`);
      }
      
      if (!session?.user) {
        console.error('❌ No authenticated user in addToGarage');
        throw new Error('You must be logged in to add items to your collection');
      }
      
      const user = session?.user;
      console.log('🔐 Authenticated user confirmed:', user?.id);

      let detectedItemType = itemType;
      let itemExists = null;

      // Auto-detect item type if not provided
      if (!itemType) {
        console.log('🔍 Auto-detecting item type for ID:', itemId);
        
        // Try products first
        const { data: productExists, error: productError } = await supabase
          ?.from('products')
          ?.select('id, name')
          ?.eq('id', itemId)
          ?.maybeSingle();

        console.log('🔍 Product check result:', { productExists, productError });

        if (productExists) {
          detectedItemType = 'product';
          itemExists = productExists;
          console.log('✅ Detected as product:', itemId, productExists?.name);
        } else {
          // Try components
          const { data: componentExists, error: componentError } = await supabase
            ?.from('components')
            ?.select('id, name')
            ?.eq('id', itemId)
            ?.maybeSingle();

          console.log('🔍 Component check result:', { componentExists, componentError });

          if (componentExists) {
            detectedItemType = 'component';
            itemExists = componentExists;
            console.log('✅ Detected as global component:', itemId, componentExists?.name);
          } else {
              // Try seller_components (very common from catalog component list)
              const { data: sellerComponentExists, error: sellerComponentError } = await supabase
                ?.from('seller_components')
                ?.select('id, custom_name, component_id, component:components(name)')
                ?.eq('id', itemId)
                ?.maybeSingle();
                
              console.log('🔍 Seller Component check result:', { sellerComponentExists, sellerComponentError });
              
              if (sellerComponentExists) {
                  detectedItemType = 'seller_component';
                  itemExists = sellerComponentExists;
                  console.log('✅ Detected as seller component:', itemId, sellerComponentExists?.custom_name || sellerComponentExists?.component?.name);
              }
          }
        }
      } else {
        // Validate provided item type
        const tableName = itemType === 'product' ? 'products' : 'components';
        console.log('🔍 Validating provided item type:', itemType, 'in table:', tableName);
        
        const { data, error } = await supabase
          ?.from(tableName)
          ?.select('id, name')
          ?.eq('id', itemId)
          ?.maybeSingle();
          
        console.log('🔍 Validation result:', { data, error });
        itemExists = data;
        console.log(`✅ Validated ${itemType}:`, itemId, data?.name);
      }

      // If item doesn't exist in either table, throw error
      if (!itemExists || !detectedItemType) {
        console.error('❌ Item not found:', itemId);
        throw new Error(`Item with ID ${itemId} does not exist. Please refresh and try again.`);
      }

      console.log('🎯 Final item details:', { 
        itemId, 
        detectedItemType, 
        itemName: itemExists?.name 
      });

      // Check if already in collection
      let finalComponentId = itemId;
      let checkField = 'product_id';
      let finalItemType = detectedItemType;
      
      if (detectedItemType === 'product') {
          checkField = 'product_id';
      } else if (detectedItemType === 'component') {
          checkField = 'component_id';
      } else if (detectedItemType === 'seller_component') {
          checkField = 'component_id';
          finalComponentId = itemExists.component_id; // map to global
          finalItemType = 'component'; // store as component type since we tracked the global ID
      }
      
      console.log('🔍 Checking if item already exists in collection...');
      
      const { data: existing, error: checkError } = await supabase
        ?.from('user_garage')
        ?.select('id, source, added_at')
        ?.eq('user_id', user?.id)
        ?.eq(checkField, finalComponentId)
        ?.maybeSingle();

      console.log('🔍 Existing check result:', { existing, checkError });

      if (checkError && checkError?.code !== 'PGRST116') {
        console.error('❌ Error checking existing:', checkError);
        throw new Error(`Database error while checking collection: ${checkError?.message}`);
      }

      if (existing) {
        console.log('⚠️ Item already in collection:', finalComponentId, existing);
        throw new Error('This item is already in your collection');
      }

      // ✅ CRITICAL FIX: Remove explicit added_at to let database handle timestamp
      const insertData = {
        user_id: user?.id,
        notes,
        source,
        item_type: finalItemType
        // ❌ REMOVED: added_at - Let database set this with CURRENT_TIMESTAMP default
      };

      // Set either product_id or component_id based on type
      if (finalItemType === 'product') {
        insertData.product_id = finalComponentId;
        insertData.component_id = null;
      } else {
        insertData.component_id = finalComponentId;
        insertData.product_id = null;
      }

      console.log('📝 Attempting to insert into user_garage:', insertData);

      const { data, error } = await supabase
        ?.from('user_garage')
        ?.insert([insertData])
        ?.select(`
          *,
          product:products (
            id,
            name,
            description,
            price,
            thumbnail_path
          ),
          component:components (
            id,
            name,
            description,
            price
          )
        `)
        ?.single();

      if (error) {
        console.error('❌ Database insert error details:', {
          error: error,
          message: error?.message,
          code: error?.code,
          details: error?.details,
          hint: error?.hint,
          insertData
        });
        
        // Provide user-friendly error messages
        if (error?.code === '23505') {
          throw new Error('This item is already in your collection');
        } else if (error?.code === '23503') {
          throw new Error('Invalid item reference. Please refresh and try again.');
        } else if (error?.code === '42501') {
          throw new Error('Permission denied. Please log in and try again.');
        } else if (error?.code === '23502') {
          // ✅ NEW: Handle NOT NULL constraint violations explicitly
          throw new Error('Failed to add item - missing required field. Please try again.');
        } else {
          throw new Error(`Failed to add item to collection: ${error?.message}`);
        }
      }

      if (!data) {
        console.error('❌ No data returned from insert operation');
        throw new Error('Failed to add item to collection - no data returned');
      }

      console.log('✅ Successfully inserted into user_garage:', data);

      // ✅ CRITICAL FIX: Verify the data was actually persisted by re-fetching
      console.log('🔍 Verifying data persistence by re-fetching from database...');
      const { data: verifyData, error: verifyError } = await supabase
        ?.from('user_garage')
        ?.select('id, user_id, product_id, component_id, source, added_at')
        ?.eq('id', data?.id)
        ?.maybeSingle();

      if (verifyError || !verifyData) {
        console.error('❌ Verification failed - data not persisted:', { verifyError, verifyData });
        throw new Error('Failed to verify item was added to collection. Please refresh and try again.');
      }

      console.log('✅ Data persistence verified:', verifyData);

      return {
        id: data?.id,
        userId: data?.user_id,
        productId: data?.product_id,
        componentId: data?.component_id,
        itemType: data?.item_type,
        notes: data?.notes,
        addedAt: data?.added_at,
        source: data?.source,
        // ✅ Include verified status for UI confidence
        verified: true
      };
    } catch (error) {
      console.error('❌ Complete error in addToGarage:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        itemId,
        itemType,
        source
      });
      throw error;
    }
  },

  /**
   * Remove a product OR component from user's garage/collection
   * @param {number} itemId - ID of product or component to remove
   * @param {string} itemType - Optional: 'product' or 'component' (auto-detects if not provided)
   */
  async removeFromGarage(itemId, itemType = null) {
    try {
      console.log('🗑️ removeFromGarage called with:', { itemId, itemType });

      // ✅ CRITICAL FIX: Enhanced authentication check
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError) {
        console.error('❌ Session error in removeFromGarage:', sessionError);
        throw new Error(`Authentication error: ${sessionError?.message}`);
      }
      
      if (!session?.user) {
        console.error('❌ No authenticated user in removeFromGarage');
        throw new Error('You must be logged in to remove items from your collection');
      }

      const user = session?.user;
      console.log('🔐 Authenticated user confirmed for removal:', user?.id);

      // CRITICAL FIX: Enhanced removal logic with better error handling
      let removalSuccess = false;
      let deletedAs = null;

      // Handle seller_components which pass their own ID instead of the global ID
      let finalComponentId = itemId;
      if (!itemType || itemType === 'component' || itemType === 'seller_component') {
          const { data: sellerComponentExists } = await supabase
            ?.from('seller_components')
            ?.select('component_id')
            ?.eq('id', itemId)
            ?.maybeSingle();
            
          if (sellerComponentExists) {
              finalComponentId = sellerComponentExists.component_id;
              console.log('🔄 Mapped seller_component ID to global component ID for removal:', finalComponentId);
          }
      }

      if (itemType === 'product' || !itemType) {
        console.log('🗑️ Attempting to remove as product...');
        const { data, error: productError } = await supabase
          ?.from('user_garage')
          ?.delete()
          ?.eq('user_id', user?.id)
          ?.eq('product_id', itemId)
          ?.select();

        console.log('🗑️ Product removal result:', { data, productError });

        // If successfully deleted as product, return
        if (!productError && data && data?.length > 0) {
          console.log('✅ Successfully removed as product:', data);
          removalSuccess = true;
          deletedAs = 'product';
        }
      }

      if (!removalSuccess && (itemType === 'component' || itemType === 'seller_component' || !itemType)) {
        console.log('🗑️ Attempting to remove as component...');
        const { data, error: componentError } = await supabase
          ?.from('user_garage')
          ?.delete()
          ?.eq('user_id', user?.id)
          ?.eq('component_id', finalComponentId)
          ?.select();

        console.log('🗑️ Component removal result:', { data, componentError });

        if (!componentError && data && data?.length > 0) {
          console.log('✅ Successfully removed as component:', data);
          removalSuccess = true;
          deletedAs = 'component';
        } else if (componentError) {
          console.error('❌ Component removal error:', componentError);
          throw new Error(`Failed to remove item from collection: ${componentError?.message}`);
        }
      }

      if (!removalSuccess) {
          console.warn('⚠️ Item not found in collection for removal:', itemId);
          // It's safe to just resolve successfully here instead of throwing
          // since the end goal (item not in DB) is achieved.
        }

        console.log('✅ Successfully removed from garage:', {
          itemId,
          deletedAs
        });
      return { success: true, deletedAs };
    } catch (error) {
      console.error('❌ Complete error in removeFromGarage:', {
        name: error?.name,
        message: error?.message,
        itemId,
        itemType
      });
      throw error;
    }
  },

  /**
   * Update notes for a product OR component in user's garage
   */
  async updateNotes(itemId, notes, itemType = 'product') {
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError) throw new Error(`Authentication error: ${sessionError.message}`);
      const user = session?.user;
      if (!user) throw new Error('Not authenticated');

      const idField = itemType === 'product' ? 'product_id' : 'component_id';

      const { data, error } = await supabase
        ?.from('user_garage')
        ?.update({ notes })
        ?.eq('user_id', user?.id)
        ?.eq(idField, itemId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        productId: data?.product_id,
        componentId: data?.component_id,
        itemType: data?.item_type,
        notes: data?.notes,
        addedAt: data?.added_at
      };
    } catch (error) {
      console.error('Error updating notes:', error);
      throw error;
    }
  },

  /**
   * Check if a product OR component is in user's garage
   */
  async isInGarage(itemId, itemType = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return false;

      // Try both product_id and component_id if type not specified
      if (!itemType || itemType === 'product') {
        const { data: productData } = await supabase
          ?.from('user_garage')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.eq('product_id', itemId)
          ?.maybeSingle();

        if (productData) return true;
      }

      if (!itemType || itemType === 'component') {
        const { data: componentData } = await supabase
          ?.from('user_garage')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.eq('component_id', itemId)
          ?.maybeSingle();

        if (componentData) return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking garage:', error);
      return false;
    }
  }
};